// ─── PER-THREAD MODEL SWITCH ─────────────────────────────────────────────────
// Haley, 2026-10-06: "add the switch to ALL". A writer can send one thread (a
// DM, a story, a channel, a group, a phone line) to a stronger model than their
// Settings default, at that model's price. The choice is the writer's own row
// (thread_model_overrides); the proxy and the phone accept it only from the
// same allowlist below, through the writer's own OpenRouter key.
//
// Prices are OpenRouter's list prices on 2026-10-06, per million tokens, for
// the label only; the writer's bill is whatever the host charges.
import { supabase } from './supabaseClient.js';

export const MODEL_CHOICES = [
  { id: '', label: 'Default (your Settings model)', note: 'DeepSeek V4 Flash for both of you today: the cheapest, and the one every measurement was taken on.' },
  { id: 'deepseek/deepseek-v4-pro', label: 'DeepSeek V4 Pro', note: 'Same family, more careful. In $0.21/M (about 10× Flash), out $0.42/M (a third of Flash). No content limits.' },
  { id: 'anthropic/claude-sonnet-5.5', label: 'Claude Sonnet 5.5', note: 'Strongest prose. In $2/M, out $10/M (about 100× Flash on input). May refuse explicit scenes.' },
];
export const ALLOWED_MODEL_OVERRIDES = new Set(MODEL_CHOICES.map(c => c.id).filter(Boolean));

const cache = new Map(); // `${type}:${id}` -> model ('' = default)
const key = (t, id) => `${t}:${id}`;

/** The writer's model for this thread ('' = their Settings default). Cached per session; refreshed on set. */
export async function getThreadModel(userId, threadType, threadId) {
  if (!userId || !threadType || !threadId) return '';
  const k = key(threadType, threadId);
  if (cache.has(k)) return cache.get(k);
  try {
    const { data } = await supabase.from('thread_model_overrides').select('model').eq('user_id', userId).eq('thread_type', threadType).eq('thread_id', String(threadId)).maybeSingle();
    const m = data?.model && ALLOWED_MODEL_OVERRIDES.has(data.model) ? data.model : '';
    cache.set(k, m);
    return m;
  } catch { return ''; }
}

/** Set (or clear with '') the writer's model for this thread. */
export async function setThreadModel(userId, threadType, threadId, model) {
  if (!userId || !threadType || !threadId) return false;
  const k = key(threadType, threadId);
  const m = model && ALLOWED_MODEL_OVERRIDES.has(model) ? model : '';
  try {
    if (!m) {
      const { error } = await supabase.from('thread_model_overrides').delete().eq('user_id', userId).eq('thread_type', threadType).eq('thread_id', String(threadId));
      if (error) throw error;
    } else {
      const { error } = await supabase.from('thread_model_overrides').upsert({ user_id: userId, thread_type: threadType, thread_id: String(threadId), model: m, updated_at: new Date().toISOString() }, { onConflict: 'user_id,thread_type,thread_id' });
      if (error) throw error;
    }
    cache.set(k, m);
    return true;
  } catch (e) {
    console.warn('[threadModel] could not save the model choice:', e?.message || e);
    return false;
  }
}

/** Synchronous read of what this session already loaded ('' when unknown). */
export function peekThreadModel(threadType, threadId) {
  return cache.get(key(threadType, threadId)) || '';
}
